import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  AUTH_EXPIRED_EVENT,
  adminLoginUser,
  getDashboard,
  loginUser,
  logoutUser,
  registerUser,
  tokenStore,
  userStore,
} from "./api";

const AuthContext = createContext(null);

function normalise(value) {
  return String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ");
}

function accountMatchesMode(user, mode) {
  const roleCode = normalise(user?.role_code);
  const designation = normalise(
    user?.designation_code || user?.designation
  );

  switch (mode) {
    case "DATA_SCIENTIST":
      return (
        designation === "data scientist" ||
        designation === "trainee data scientist"
      );
    case "FACULTY":
      return designation === "faculty";
    case "ADMIN":
      return roleCode === "admin" || roleCode === "main manager" || roleCode === "director";
    case "USER":
      return roleCode !== "admin" && roleCode !== "main manager";
    default:
      return false;
  }
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    Boolean(tokenStore.getAccess())
  );

  useEffect(() => {
    const onExpired = () => setIsAuthenticated(false);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const login = useCallback(async (username, password, mode = "DATA_SCIENTIST") => {
    const credentials = { username, password };

    // IMPORTANT: Admin uses the dedicated backend .env credential endpoint.
    // Other portal users continue to use normal username/password login.
    const data = mode === "ADMIN"
      ? await adminLoginUser(credentials)
      : await loginUser(credentials);

    if (!data.access) {
      throw new Error("Login succeeded without an access token.");
    }

    tokenStore.set(data.access, data.refresh);

    try {
      const dashboard = await getDashboard();
      if (!accountMatchesMode(dashboard?.user, mode)) {
        throw new Error(
          `This account is not authorised for ${mode.replace(/_/g, " ").toLowerCase()} sign-in.`
        );
      }

      userStore.set(dashboard.user);
    } catch (error) {
      tokenStore.clear();
      setIsAuthenticated(false);
      throw error;
    }

    setIsAuthenticated(true);
    return data;
  }, []);

  const register = useCallback(async (formData) => {
    // The backend may resolve an existing account by its registered email and
    // return its canonical Django username. Always use that username for login.
    const enrolledAccount = await registerUser(formData);
    const loginUsername = enrolledAccount?.username || formData.username;
    await login(loginUsername, formData.password, "USER");
  }, [login]);

  const logout = useCallback(() => {
    logoutUser();
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
